import { useCallback, useEffect, useState } from 'react';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
} from 'react-hook-form';
import {
  BaseellaStatus,
  BaseTransaction,
  PaymentType,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppSelector } from '@store';
import useRetry from '../../outgoing/helpers/use-retry';
import { useLazyGetStatusesTransactionByIdQuery } from '../../../store/statuses/services';
import {
  useCheckNostroTransactionMutation,
  useCreateNostroTransactionMutation,
  useLazyGetNostroListQuery,
  useLazyGetNostroTransactionByIdQuery,
  useUpdateNostroTransactionMutation,
  useLazyGetNostroIntegrationLogsQuery,
} from '../store/services';
import StatusesUrl from '../../../types/statuses-url';
import { UpdateNostroTransaction } from '../types';
import {
  CheckTransaction,
  CheckTransactionStatus,
} from '../../../types/check-transaction';
import { useLazyGetTransactionsPurposeCodesSettingsQuery } from '../../../store/dictionaries/services';
import { PspPartner } from '../../../types/psp-partner';
import { PurposeCodeSettings } from '../../../store/types';

const useRequests = ({
  activePermissions,
  transactionId,
  setErrorMessage,
  setStatusChangeError,
  errors,
  getValues,
  setError,
  transaction,
  setCheckError,
  fields,
  setStatusCheckError,
  clearErrors,
  toPspPartnerList,
  purposeCodeSettingItem,
}: {
  activePermissions?: PermissionEnum[];
  transactionId?: string;
  setErrorMessage: (data: string) => void;
  setStatusChangeError: (data: string) => void;
  errors: FieldErrors<any>;
  getValues: UseFormGetValues<any>;
  setError: UseFormSetError<any>;
  transaction?: BaseTransaction;
  fields: any;
  setCheckError: (data: string) => void;
  setStatusCheckError: (data: string) => void;
  clearErrors: UseFormClearErrors<any>;
  toPspPartnerList?: PspPartner[];
  purposeCodeSettingItem?: PurposeCodeSettings;
}) => {
  const [checkStatus, setCheckStatus] = useState<
    CheckTransactionStatus | undefined
  >(undefined);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [acceptedInWithTime, setAcceptedInWithTime] = useState(false);
  const [acceptedOutWithTime, setAcceptedOutWithTime] = useState(false);
  const [transferDateWithTime, setTransferDateWithTime] = useState(false);

  const { purposeCodesSettings } = useAppSelector(
    (state) => state.transactionsDictionaries,
  );

  const [
    getStatusesTransactionById,
    {
      isLoading: isLoadingStatuses,
      isFetching: isFetchingStatuses,
      isUninitialized: isUninitializedStatuses,
    },
  ] = useLazyGetStatusesTransactionByIdQuery();

  const [getNostroTransactionById, { isLoading, isUninitialized }] =
    useLazyGetNostroTransactionByIdQuery();

  const [create, { isLoading: createIsLoading, isSuccess: createIsSuccess }] =
    useCreateNostroTransactionMutation();

  const [update, { isLoading: editIsLoading, isError: editIsError }] =
    useUpdateNostroTransactionMutation();
  const [checkPayment, { isLoading: checkInProgress }] =
    useCheckNostroTransactionMutation();

  const [getTransactionsList] = useLazyGetNostroListQuery();
  const [getPurposeCodeSettings] =
    useLazyGetTransactionsPurposeCodesSettingsQuery();

  const [
    getIntegrationLogs,
    {
      isLoading: integrationLogsIsLoading,
      isFetching: integrationLogsIsFetching,
    },
  ] = useLazyGetNostroIntegrationLogsQuery();

  const { getTransaction, onClearTimer, internalError } = useRetry({
    getTransactionFunction: getNostroTransactionById,
    id: transactionId as string,
    isRetry: true,
    getStatusesFunction: getStatusesTransactionById,
    statusPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_NOSTRO_EDIT_ACTION,
    ),
    statusUrl: StatusesUrl.NOSTRO,
  });

  const abortController = (() => {
    let controller = new AbortController();
    let controllerSignal = controller.signal;

    const abortPendingRequest = () => {
      controller.abort();
      // eslint-disable-next-line react-hooks/immutability
      controller = new AbortController();
      controllerSignal = controller.signal;
    };

    const getSignalToken = () => controllerSignal;

    return {
      abortPendingRequest,
      getSignalToken,
    };
  })();

  const onSubmit = async () => {
    let response;

    if (!Object.keys(errors).length) {
      setErrorMessage('');
      setStatusChangeError('');

      const values = getValues();
      const body = {} as UpdateNostroTransaction;

      if (values?.acceptedIn) {
        if (acceptedInWithTime) {
          body.acceptedIn = new Date(values?.acceptedIn).toUTCString();
        } else {
          body.acceptedIn = values?.acceptedIn;
        }
      } else {
        body.acceptedIn = undefined;
      }

      if (values?.acceptedOut) {
        if (acceptedOutWithTime) {
          body.acceptedOut = new Date(values?.acceptedOut).toUTCString();
        } else {
          body.acceptedOut = values?.acceptedOut;
        }
      } else {
        body.acceptedOut = undefined;
      }

      if (transaction?.baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS) {
        if (values.senderAccountNumber) {
          body.senderAccountNumber = values.senderAccountNumber;
        }
      }

      response = (await update({ body, id: transactionId as string })) as {
        data?: BaseTransaction;
        error: ResponseError;
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      } else {
        getStatusesTransactionById({
          id: transactionId as string,
          url: StatusesUrl.NOSTRO,
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as string,
        });
      }
    }

    return response;
  };

  const checkPaymentAction = useCallback(async () => {
    clearErrors(['caspId', 'sum']);

    setCheckError('');
    setStatusCheckError('');

    abortController.abortPendingRequest();

    const body: CheckTransaction = {
      paymentType: fields.paymentType,
      sum: Number(fields.sum),
      senderAccountNumber: fields.senderAccountNumber,
      recipientAccountNumber: fields.recipientAccountNumber,
      currencyId: fields.currencyId,
      info: fields.info,
    };

    if (
      fields.paymentType === PaymentType.CRYPTO_ASSET_PAYMENT &&
      fields.caspId
    ) {
      body.caspId = fields.caspId;
    }

    if (fields.purposeCode && purposeCodeSettingItem) {
      body.purposeCode = fields.purposeCode;
    }

    if (fields.toPartnerPspId) {
      const currentPspPartner = toPspPartnerList?.find(
        (item) => item.id === fields.toPartnerPspId,
      );

      if (currentPspPartner && currentPspPartner.countryId) {
        body.benificiaryBankCountry = currentPspPartner.countryId;
      }
    }

    const response = (await checkPayment({
      body,
      abortController,
    })) as {
      data?: any;
      error?: { data: { message?: string }; message?: string } | ResponseError;
    };

    if ('data' in response) {
      setCheckStatus(response.data.data.status);

      if (response?.data?.data?.errorMessage) {
        setStatusCheckError(response?.data?.data?.errorMessage);
      }

      if (response.data.data.status === CheckTransactionStatus.GREEN) {
        setSubmitDisabled(false);
      }
    } else {
      setSubmitDisabled(true);

      if (response && response.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setCheckError,
        });
      }
    }
  }, [
    checkPayment,
    fields.paymentType,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    fields.info,
    fields.caspId,
    fields.purposeCode,
    fields.toPartnerPspId,
    purposeCodeSettingItem,
  ]);

  useEffect(() => {
    if (transactionId) {
      setTransferDateWithTime(true);
      setAcceptedInWithTime(true);
      setAcceptedOutWithTime(true);
    }
  }, [transactionId]);

  useEffect(() => {
    if (
      !purposeCodesSettings &&
      activePermissions?.includes(PermissionEnum.PURPOSE_CODE_SETTINGS_LIST)
    ) {
      getPurposeCodeSettings();
    }
  }, [purposeCodesSettings, activePermissions]);

  return {
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    isLoading,
    isUninitialized,
    create,
    createIsLoading,
    createIsSuccess,
    editIsLoading,
    editIsError,
    getTransactionsList,
    getTransaction,
    onClearTimer,
    internalError,
    onSubmit,
    getStatusesTransactionById,
    checkPaymentAction,
    checkStatus,
    setCheckStatus,
    checkInProgress,
    submitDisabled,
    setSubmitDisabled,
    getIntegrationLogs,
    integrationLogsIsLoading,
    integrationLogsIsFetching,
    transferDateWithTime,
    acceptedInWithTime,
    acceptedOutWithTime,
    setTransferDateWithTime,
    setAcceptedInWithTime,
    setAcceptedOutWithTime,
  };
};

export default useRequests;
